import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { JSONContent } from "@tiptap/core";
import { pick } from "lodash-es";
import { nanoid } from "nanoid";
import React, { createContext, useContext, useMemo } from "react";
import { useParams } from "react-router-dom";

import {
  CreatePageMutation,
  CreateProjectMutation,
  DeletePageMutation,
  PageInput,
  PartialPageInput,
  UpdatePageMutation,
  useCreatePageMutation,
  useCreateProjectMutation,
  useDeletePageMutation,
  useUpdatePageMutation,
} from "@/__generated__/graphql";
import { Page, Project, PromptTemplate } from "@/types";
import { deDupeContent, docIsEmpty, errorMessage, errorRequiresRecreate } from "@/utils";

import useCeramic from "../useCeramic";
import { getProjectPages } from "./pages";
import { getProjects, newProject } from "./projects";
import { createPromptTemplate, getPromptTemplates, updatePromptTemplate } from "./promptTemplates";
import { getSimilarPapers } from "./semantic";

type QueriesContextType = {
  projects: Project[];
  currentProject: Project | undefined;
  createProject: () => Promise<CreateProjectMutation>;
  // pages: Page[];
  // pagesStatic: Page[];
  nodePages: string[];
  updatePage: ({
    page,
    content,
  }: {
    page: Page;
    content: PartialPageInput;
  }) => Promise<[CreatePageMutation | UpdatePageMutation, boolean]>;
  createPage: ({ isDefault, lateralID, content }: PageInput) => Promise<CreatePageMutation>;
  deletePage: (page: Page) => Promise<DeletePageMutation>;
  currentPage: Page | undefined;
  isFetching: boolean;
  // pagesIsFetched: boolean;
  currentPageVersions: Page[];
  promptTemplates: PromptTemplate[];
  updatePromptTemplate: typeof updatePromptTemplate;
  createPromptTemplate: typeof createPromptTemplate;
  getSimilarPapers: typeof getSimilarPapers;
};

export const QueriesContext = createContext<QueriesContextType | undefined>(undefined);

export function QueriesProvider({ children }: { children: React.ReactNode }) {
  let { projectId = "global-graph", pageId = "default", projectPublicId } = useParams();
  const queryClient = useQueryClient();

  const { data: projects, isFetching: projectsIsFetching } = useQuery({
    // @ts-ignore
    queryKey: ["projects"],
    initialData: [],
    queryFn: getProjects,
  });
  const currentProject = useMemo(
    () =>
      projects?.find((p) =>
        projectPublicId ? p.publicID === projectPublicId : p.slug === projectId
      ),
    [projectId, projects]
  );

  const createProjectMutation = useCreateProjectMutation();

  const createProject = async () => {
    console.log("createProject");
    if (!projectId) throw Error("No projectId or wallet.");
    const response = await createProjectMutation.mutateAsync({
      input: { content: newProject(projectId, "") },
    });
    queryClient.invalidateQueries({ queryKey: ["projects"] });
    console.log("createProject response: ", response);
    return response;
  };

  // const {
  //   data: pages,
  //   isFetching: pagesIsFetching,
  //   isFetched: pagesIsFetched,
  // } = useQuery({
  //   // @ts-ignore
  //   queryKey: ["pages", currentProject?.id],
  //   enabled: Boolean(currentProject?.id),
  //   initialData: [],
  //   queryFn: getProjectPages,
  //   refetchOnWindowFocus: false,
  // });
  // const pagesStatic: Page[] = deDupeContent(
  //   queryClient.getQueryData(["pages", currentProject?.id]) ?? []
  // );
  const nodePages = [];
  // const nodePages = [
  //   ...new Set(pages?.filter((p) => !docIsEmpty(p.content.editorState)).map((p) => p.id)),
  // ];

  const createPageMutation = useCreatePageMutation();
  const deletePageMutation = useDeletePageMutation();
  const updatePageMutation = useUpdatePageMutation({
    onMutate: async ({ input: { id, content } }) => {
      await queryClient.cancelQueries({
        queryKey: ["pages", currentProject?.id],
      });
      const previousPages = queryClient.getQueryData(["pages", currentProject?.id]);
      // Replace the new content in the node matching the ceramicID
      queryClient.setQueryData(["pages", currentProject?.id], (old: Page[] | undefined) =>
        [...(old ?? [])].map((p) => {
          const newContent: Partial<Page> = {};
          if (content?.content) {
            newContent["content"] = JSON.parse(content.content);
          }
          if (content?.meta) {
            newContent["meta"] = JSON.parse(content.meta);
          }
          return p.ceramicID === id ? { ...p, ...newContent } : p;
        })
      );
      return { previousPages };
    },
  });

  const createPage = async ({
    isDefault = false,
    lateralID = nanoid(),
    content = "{}",
    originalAuthor = "",
    meta = "{}",
  }: PageInput) => {
    console.log("createPage");
    if (!currentProject?.id) throw Error("No project or wallet.");
    const response = await createPageMutation.mutateAsync({
      input: {
        content: {
          projectID: currentProject?.id,
          originalAuthor,
          created: new Date().toJSON(),
          deleted: false,
          meta,
          isDefault,
          lateralID,
          content,
        },
      },
    });
    queryClient.invalidateQueries({ queryKey: ["pages", currentProject?.id] });
    queryClient.invalidateQueries({ queryKey: ["default-page"] });
    queryClient.invalidateQueries({ queryKey: ["page-ids"] });
    console.log("createPage response: ", response);
    return response;
  };

  const updatePage = async ({ page, content }: { page: Page; content: PartialPageInput }) => {
    try {
      const response = await updatePageMutation.mutateAsync({
        input: {
          id: page.ceramicID,
          content,
        },
      });
      return [response, false];
    } catch (error) {
      if (!errorRequiresRecreate(error)) {
        throw error;
      }

      console.log("Recreating page from this user");
      const meta: { [key: string]: string } = {};
      if (page?.meta && typeof page.meta !== "string") {
        meta["meta"] = JSON.stringify(page.meta);
      }
      const response = await createPageMutation.mutateAsync({
        input: {
          content: {
            ...pick(page, ["projectID", "originalAuthor", "isDefault", "deleted"]),
            ...meta,
            lateralID: page.id,
            created: new Date().toJSON(),
            ...content,
          },
        },
      });
      queryClient.invalidateQueries({
        queryKey: ["pages", currentProject?.id],
      });
      return [response, true];
    }
  };

  const deletePage = async (page: Page) => {
    try {
      return await deletePageMutation.mutateAsync({ id: page.ceramicID });
    } catch (error) {
      console.log("ERROR", error);
      throw Error("Should recreate here");
      // return await recreateNodeOnError(
      //   error,
      //   { ...node, deleted: true },
      //   false
      // );
    }
  };

  const { data: promptTemplates } = useQuery({
    // @ts-ignore
    queryKey: ["prompt-templates", currentProject?.slug],
    initialData: [],
    queryFn: getPromptTemplates,
    enabled: Boolean(currentProject?.id),
  });

  const value = {
    projects: projects ?? [],
    currentProject,
    createProject,
    // pagesIsFetched,
    // pages: pages ?? [],
    // pagesStatic: pagesStatic ?? [],
    nodePages: nodePages ?? [],
    createPage,
    updatePage,
    deletePage,
    isFetching: projectsIsFetching,
    promptTemplates,
    createPromptTemplate,
    updatePromptTemplate,
    getSimilarPapers,
  };

  return <QueriesContext.Provider value={value}>{children}</QueriesContext.Provider>;
}

/**
 * Hook for querying Coordination.network
 */
export default function useQueries() {
  const context = useContext(QueriesContext);

  if (!context) {
    throw new Error(`useQueries must be used within a QueriesProvider`);
  }

  return context;
}
